
import React from "react";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  MessageCircle, 
  Gift, 
  Users, 
  Clock 
} from "lucide-react";

interface StepProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Step = ({ number, icon, title, description }: StepProps) => {
  return (
    <div className="card-highlight flex flex-col items-center text-center relative group">
      {/* Hover effect background */}
      <div className="absolute inset-0 bg-gradient-to-b from-legatia-yellow/0 to-legatia-yellow/20 opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-2xl"></div>
      
      <div className="relative mb-6">
        <div className="bg-legatia-yellow/20 rounded-full w-16 h-16 flex items-center justify-center">
          {icon}
        </div>
        <div className="absolute -top-2 -right-2 bg-legatia-orange text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
          {number}
        </div>
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const NewHowItWorks = () => {
  return (
    <section id="how-it-works" className="section-padding bg-gradient-to-b from-white to-legatia-green/5 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/4 left-[10%] w-32 h-32 bg-legatia-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-1/3 right-[15%] w-40 h-40 bg-legatia-yellow/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 right-[5%] w-20 h-20 bg-legatia-green/10 rounded-full"></div>
        <div className="absolute bottom-1/4 left-[20%] w-24 h-24 bg-legatia-blue/10 rounded-full"></div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <h2 className="section-title text-center">
          Votre présence, <span className="gradient-text">transcendant le temps</span>
        </h2>
        
        <div className="max-w-lg mx-auto mb-12">
          <div className="relative py-8">
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-legatia-orange/30 transform -translate-x-1/2"></div>
            <img 
              src="/assets/process-illustration.svg" 
              alt="Le processus Legatia" 
              className="mx-auto mb-8 max-w-xs"
            />
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
          <Step 
            number={1}
            icon={<Calendar className="h-8 w-8 text-legatia-orange" />}
            title="Identifiez le moment clé"
            description="Sélectionnez un jalon futur important où votre présence aura un impact maximum"
          />
          
          <Step 
            number={2}
            icon={<MessageCircle className="h-8 w-8 text-legatia-orange" />}
            title="Composez votre message"
            description="Partagez vos pensées, émotions, conseils et souvenirs comme si vous étiez présent à ce moment futur"
          />
          
          <Step 
            number={3}
            icon={<Gift className="h-8 w-8 text-legatia-orange" />}
            title="Associez un geste tangible"
            description="Accompagnez vos mots d'une contribution qui prendra de la valeur au fil du temps"
          />
          
          <Step 
            number={4}
            icon={<Users className="h-8 w-8 text-legatia-orange" />}
            title="Créez une présence collective"
            description="Invitez famille et amis à joindre leurs voix à la vôtre pour un impact émotionnel démultiplié"
          />
          
          <Step 
            number={5}
            icon={<Clock className="h-8 w-8 text-legatia-orange" />}
            title="Laissez l'émotion mûrir"
            description="Au moment choisi, votre présence émotionnelle se matérialise, créant un pont puissant entre le passé et le présent"
          />
        </div>
        
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4">Une technologie au service de l'émotion</h3>
              <p className="text-gray-700 mb-4">
                Chez Legatia, la technologie n'est pas une fin en soi, mais un moyen de préserver et
                transmettre ce qui compte vraiment : la connexion humaine. Votre capsule émotionnelle
                est protégée par des protocoles de sécurité avancés et des systèmes redondants qui
                garantissent sa livraison au moment parfait.
              </p>
              <p className="text-gray-700">
                C'est l'alliance parfaite entre innovation technologique et profondeur émotionnelle.
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-legatia-green/20 rounded-full transform translate-y-2"></div>
              <img 
                src="/assets/secure-process.jpg" 
                alt="Processus sécurisé" 
                className="rounded-lg shadow-md relative z-10 w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <Button className="btn-primary">Créer une capsule temporelle émotionnelle</Button>
        </div>
      </div>
    </section>
  );
};

export default NewHowItWorks;
